import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Gelwel — Social & Lifestyle App for Real Connections',
  description = 'Meet people who match your vibe. Gelwel combines lifestyle, AI, and social features into one sleek app.',
  keywords = 'social app, lifestyle app, meet people, real connections, AI matching, vibe matching, social networking, lifestyle community',
  ogImage = 'https://gelwelpartners.com/og-image.png',
  ogType = 'website',
  canonicalUrl,
}) => {
  const location = useLocation();
  const currentUrl = canonicalUrl || `https://gelwelpartners.com${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const metaTags = [
      { name: 'title', content: title },
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: currentUrl },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:url', content: currentUrl },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const attribute = property ? 'property' : 'name';
      const attributeValue = (property || name) as string;
      let element = document.querySelector(
        `meta[${attribute}="${attributeValue}"]`
      ) as HTMLMetaElement;

      if (element) {
        element.content = content;
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, attributeValue);
        element.content = content;
        document.head.appendChild(element);
      }
    });

    let canonicalLink = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = currentUrl;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = currentUrl;
      document.head.appendChild(canonicalLink);
    }
  }, [title, description, keywords, ogImage, ogType, currentUrl]);

  return null;
};

export default SEOHead;
